import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import UsersGeography from './UsersGeography';
import UserGraph from './UserGraph';
import WorkshopUsers from './WorkshopUsers';
import {
  Toolbar, Typography, Button, Menu, MenuItem, Card, CardContent, Alert, Box,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PageGuideTooltip from './PageGuideTooltip';
import { trackFeature } from './Layout';
import { formatCurrency } from '../utils/formatCurrency';
import { toRedisScoreDate } from '../utils/redisScoreDate';
import { lastMonthRange, lastQuarterRange, localDateString } from '../utils/periods';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const initStart = () => {
    const p = searchParams.get('start');
    if (p) return p;
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return localDateString(d);
  };
  const initEnd = () => {
    const p = searchParams.get('end');
    if (p) return p;
    return localDateString();
  };
  const initPeriod = () => searchParams.get('period') || 'Past 7 days';

  const [data, setData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [currentLevelName, setCurrentLevelName] = useState(searchParams.get('level') || 'geography');
  const [startDate, setStartDate] = useState(initStart);
  const [endDate, setEndDate] = useState(initEnd);
  const [dataStack, setDataStack] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [dailyUserData, setDailyUserData] = useState([]);
  const [isDailyDataLoading, setIsDailyDataLoading] = useState(true);
  const [dailyDataError, setDailyDataError] = useState(null);
  const [workshopUsersData, setWorkshopUsersData] = useState([]);
  const [selectedWorkshop, setSelectedWorkshop] = useState(searchParams.get('workshop') || null);
  const [uniqueOppTotal, setUniqueOppTotal] = useState(null);
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState(initPeriod);
  const [lastValidDateRange, setLastValidDateRange] = useState({ start: '', end: '' });

  const levels = useMemo(() => ['geography', 'region', 'area', 'district', 'userrole', 'l2manager', 'manager'], []);

  // Sync state → URL params
  useEffect(() => {
    const params = {};
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    if (currentLevelName && currentLevelName !== 'geography') params.level = currentLevelName;
    if (selectedWorkshop) params.workshop = selectedWorkshop;
    if (selectedPeriodLabel && selectedPeriodLabel !== 'Past 7 days') params.period = selectedPeriodLabel;
    setSearchParams(params, { replace: true });
  }, [startDate, endDate, currentLevelName, selectedWorkshop, selectedPeriodLabel, setSearchParams]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const formattedStartDate = toRedisScoreDate(startDate);
      const formattedEndDate = toRedisScoreDate(endDate);
      const url = `${API_BASE_URL}/users/${formattedStartDate}&${formattedEndDate}`;
      const response = await axios.get(url);

      if (response.data && Array.isArray(response.data)) {
        const parsedData = response.data.map(item => {
          try {
            return typeof item === 'string' ? JSON.parse(item) : item;
          } catch (e) {
            console.error('Error parsing item:', item, e);
            return null;
          }
        }).filter(item => item !== null);

        setData(parsedData);
        setCurrentData(parsedData);
        setDataStack([parsedData]);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response) {
        setError(`Failed to fetch data: Server responded with status ${error.response.status}`);
      } else if (error.request) {
        setError('Failed to fetch data: No response received from server');
      } else {
        setError(`Failed to fetch data: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  const fetchDailyUserData = useCallback(async () => {
    setIsDailyDataLoading(true);
    setDailyDataError(null);
    try {
      const formattedStartDate = toRedisScoreDate(startDate);
      const formattedEndDate = toRedisScoreDate(endDate);
      const url = `${API_BASE_URL}/daily/${formattedStartDate}&${formattedEndDate}`;
      const response = await axios.get(url);

      if (response.data && Array.isArray(response.data)) {
        const parsedData = response.data.map(item => {
          try {
            return typeof item === 'string' ? JSON.parse(item) : item;
          } catch (e) {
            console.error('Error parsing daily user data item:', item, e);
            return null;
          }
        }).filter(item => item !== null);

        setDailyUserData(parsedData);
      } else {
        throw new Error('Invalid daily user data format received from server');
      }
    } catch (error) {
      console.error('Error fetching daily user data:', error);
      setDailyDataError(`Failed to fetch daily user data: ${error.message}`);
    } finally {
      setIsDailyDataLoading(false);
    }
  }, [startDate, endDate]);

  const fetchUniqueOppTotal = useCallback(async () => {
    if (startDate && endDate) {
      try {
        const response = await axios.get(`${API_BASE_URL}/unique-opp-total`, {
          params: { startDate, endDate }
        });
        setUniqueOppTotal(response.data.total);
      } catch (error) {
        console.error('Error fetching unique opp total:', error);
        setUniqueOppTotal(null);
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && startDate === endDate) {
      const nextDay = new Date(startDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayString = nextDay.toISOString().split('T')[0];
      setEndDate(nextDayString);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
      fetchDailyUserData();
      fetchUniqueOppTotal();
    }
  }, [fetchData, fetchDailyUserData, fetchUniqueOppTotal, startDate, endDate]);

  const handleDrillDown = useCallback((drillDownData, level, levelName) => {
    setCurrentData(drillDownData);
    setCurrentLevelName(levelName);
    setDataStack(prevStack => [...prevStack, drillDownData]);
    trackFeature('dashboard_drilldown', { level: levelName });
  }, []);

  const handleBackClick = useCallback(() => {
    if (dataStack.length > 1) {
      const newStack = dataStack.slice(0, -1);
      setDataStack(newStack);
      setCurrentData(newStack[newStack.length - 1]);
      setCurrentLevelName(levels[newStack.length - 1] || levels[0]);
    }
  }, [dataStack, levels]);

  const handleTopClick = useCallback(() => {
    if (dataStack.length > 1) {
      const topLevelData = dataStack[0];
      setDataStack([topLevelData]);
      setCurrentData(topLevelData);
      setCurrentLevelName(levels[0] || 'geography');
    }
  }, [dataStack, levels]);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'startDate') {
      setStartDate(value);
    } else if (name === 'endDate') {
      setEndDate(value);
    }
    setSelectedPeriodLabel('');
  }, []);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDateRangeSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(localDateString(start));
    setEndDate(localDateString(end));
    setSelectedPeriodLabel(`Past ${days} days`);
    trackFeature('dashboard_date_range', { days });
    handleMenuClose();
  };

  const handleCalendarMenuClick = (event) => setCalendarAnchorEl(event.currentTarget);
  const handleCalendarMenuClose = () => setCalendarAnchorEl(null);

  const handleCalendarPeriodSelect = (period) => {
    const { startDate: start, endDate: end } = period === 'lastMonth' ? lastMonthRange() : lastQuarterRange();
    setStartDate(start);
    setEndDate(end);
    setSelectedPeriodLabel(period === 'lastMonth' ? 'Last Calendar Month' : 'Last Calendar Quarter');
    handleCalendarMenuClose();
  };

  const processWorkshopUsersData = useCallback((data) => {
    const workshopCounts = data.reduce((acc, item) => {
      const workshop = item.workshop || 'Unknown';
      acc[workshop] = (acc[workshop] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(workshopCounts).map(([name, value]) => ({ name, value }));
  }, []);

  const handleWorkshopSelect = useCallback((workshop) => {
    setSelectedWorkshop(workshop);
    const filteredData = data.filter(item => item.workshop === workshop);
    setCurrentData(filteredData);
  }, [data]);

  const handleClearWorkshop = useCallback(() => {
    setSelectedWorkshop(null);
    setCurrentData(data);
  }, [data]);

  useEffect(() => {
    const processedData = processWorkshopUsersData(currentData);
    setWorkshopUsersData(processedData);
  }, [currentData, processWorkshopUsersData, selectedWorkshop]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = useCallback(({ value, componentType }) => {
    let clickedDate;
    if (typeof value === 'string') {
      clickedDate = value;
    } else if (typeof value === 'number') {
      const index = dailyUserData.usernum?.indexOf(value);
      if (index !== -1 && dailyUserData.userday) {
        clickedDate = dailyUserData.userday[index];
      }
    }

    if (clickedDate) {
      setLastValidDateRange({ start: startDate, end: endDate });
      try {
        const date = new Date(clickedDate + ', ' + new Date().getFullYear());
        const formattedDate = formatDate(date);
        setStartDate(formattedDate);
        setEndDate(formattedDate);
      } catch (error) {
        console.error('Error processing date click:', error);
      }
    }
  }, [dailyUserData, startDate, endDate]);

  const handleReturnToPreviousRange = useCallback(() => {
    if (lastValidDateRange.start && lastValidDateRange.end) {
      setStartDate(lastValidDateRange.start);
      setEndDate(lastValidDateRange.end);
      setLastValidDateRange({ start: '', end: '' });
    }
  }, [lastValidDateRange]);

  return (
    <>
      {/* Secondary toolbar with date controls */}
      <Toolbar
        variant="dense"
        style={{
          minHeight: '32px',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px',
          backgroundColor: 'white',
          color: 'black',
          padding: '4px 12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
      >
        <Button
          onClick={handleMenuClick}
          style={{ marginRight: '8px', color: 'black', padding: '2px 6px', fontSize: '0.9rem' }}
          endIcon={<ArrowDropDownIcon />}
        >
          Past Days
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {['Past 7 days', 'Past 30 days', 'Past 60 days', 'Past 90 days'].map((label, i) => (
            <MenuItem key={label} onClick={() => handleDateRangeSelect([7, 30, 60, 90][i])} sx={{ fontSize: '0.9rem' }}>
              <span style={{ width: '1.2em', display: 'inline-block', textAlign: 'center', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: 1 }}>
                {selectedPeriodLabel === label ? '●' : ''}
              </span>
              {label}
            </MenuItem>
          ))}
        </Menu>
        <Button
          onClick={handleCalendarMenuClick}
          style={{ marginRight: '16px', color: 'black', padding: '2px 6px', fontSize: '0.9rem' }}
          endIcon={<ArrowDropDownIcon />}
        >
          Calendar Period
        </Button>
        <Menu anchorEl={calendarAnchorEl} open={Boolean(calendarAnchorEl)} onClose={handleCalendarMenuClose}>
          {[['Last Calendar Month', 'lastMonth'], ['Last Calendar Quarter', 'lastQuarter']].map(([label, period]) => (
            <MenuItem key={label} onClick={() => handleCalendarPeriodSelect(period)} sx={{ fontSize: '0.9rem' }}>
              <span style={{ width: '1.2em', display: 'inline-block', textAlign: 'center', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: 1 }}>
                {selectedPeriodLabel === label ? '●' : ''}
              </span>
              {label}
            </MenuItem>
          ))}
        </Menu>
        <Typography variant="body2" style={{ marginRight: '6px', color: 'black', fontSize: '0.75rem' }}>
          Start:
        </Typography>
        <input
          type="date"
          name="startDate"
          value={startDate}
          onChange={handleDateChange}
          style={{ marginRight: '16px', padding: '1px 2px', backgroundColor: 'transparent', color: 'black', border: '1px solid black', fontSize: '0.75rem' }}
        />
        <Typography variant="body2" style={{ marginRight: '6px', color: 'black', fontSize: '0.75rem' }}>
          End:
        </Typography>
        <input
          type="date"
          name="endDate"
          value={endDate}
          onChange={handleDateChange}
          style={{ padding: '1px 2px', backgroundColor: 'transparent', color: 'black', border: '1px solid black', fontSize: '0.75rem' }}
        />
        {selectedPeriodLabel && (
          <Typography sx={{ ml: 2, fontSize: '1.1rem', fontWeight: 'bold', color: 'black' }}>
            The current view is <span style={{ color: 'rgb(218,41,28)' }}>{selectedPeriodLabel}</span>
          </Typography>
        )}
      </Toolbar>

      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{ borderRadius: 0, borderLeft: '4px solid #1976d2', '& .MuiAlert-icon': { color: '#1976d2' } }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <strong>Participation Dashboard</strong>
          <PageGuideTooltip
            title="Participation Dashboard" metricKey="attendee_definition"
            description="Attendance activity from Google Analytics check-in events: sessions, users, new users, and linked opportunity value. Use the geographic drill-down to compare by region, area, and district."
            howtoHref="/howto#entry-9"
          />
        </Box>
        {' '}— workshop attendance activity sourced from Google Analytics check-in events, aggregated daily into Redis. Shows total sessions, unique users, new users, and linked opportunity value for the selected period. Use the geographic drill-down to compare participation across regions, areas, and districts. This is an operational view — it tracks who showed up, not whether deals closed.
      </Alert>

      {/* Main content */}
      <div style={{ margin: '12px auto', maxWidth: '95%', padding: '0 4px' }}>
        {isDailyDataLoading ? (
          <p>Loading daily user data...</p>
        ) : dailyDataError ? (
          <p>{dailyDataError}</p>
        ) : dailyUserData && dailyUserData.length > 0 ? (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '40px', flexWrap: 'wrap' }}>
              {(() => {
                const oppAmtDisplay = uniqueOppTotal > 0
                  ? formatCurrency(uniqueOppTotal)
                  : (uniqueOppTotal === null ? 'N/A' : '$0');
                const cards = [
                  { title: 'Total Sessions', value: dailyUserData[0]?.sessions },
                  { title: 'Total Users', value: dailyUserData[0]?.users },
                  { title: 'New Users', value: dailyUserData[0]?.newusers },
                  { title: 'Opportunities', value: oppAmtDisplay }
                ];
                return cards.map((item, index) => (
                  <Card key={index} sx={{
                    flex: '1 1 0',
                    minWidth: '200px',
                    height: '202.5px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    borderRadius: '12px'
                  }}>
                    <CardContent sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      padding: '24px'
                    }}>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '1.26rem', marginBottom: '16px' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', fontSize: '2.25rem' }}>
                        {item.value || 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
            <Card sx={{ margin: '20px auto', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ padding: '24px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <Typography variant="body2">Data ranges can be limited by clicking on the charts</Typography>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '16px',
                  position: 'relative'
                }}>
                  <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                    User Activity
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReturnToPreviousRange}
                    disabled={!lastValidDateRange.start || !lastValidDateRange.end}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      color: (lastValidDateRange.start && lastValidDateRange.end) ? 'black' : 'rgba(0, 0, 0, 0.26)',
                      borderColor: (lastValidDateRange.start && lastValidDateRange.end) ? 'black' : 'rgba(0, 0, 0, 0.12)',
                      padding: '2px 8px',
                      minWidth: '70px',
                      fontSize: '0.75rem'
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <div style={{ height: '250px' }}>
                  <UserGraph
                    data={dailyUserData[0]}
                    onDateClick={handleDateClick}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <p>No daily user data available or invalid data structure. Received: {JSON.stringify(dailyUserData)}</p>
        )}
      </div>

      <div style={{ margin: '0 auto', maxWidth: '95%' }}>
        {isLoading ? (
          <p>Loading chart data...</p>
        ) : error ? (
          <p style={{ color: 'red' }}>{error}</p>
        ) : data.length > 0 ? (
          <>
            <div style={{ display: 'flex', gap: '16px', margin: '20px 0' }}>
              <Card sx={{ flex: 1, height: '350px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', minWidth: 0 }}>
                <CardContent sx={{ height: '100%', padding: '24px' }}>
                  <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                    Users by {(currentLevelName || 'geography').charAt(0).toUpperCase() + (currentLevelName || 'geography').slice(1)}
                  </Typography>
                  <div style={{ height: 'calc(100% - 40px)', width: '100%', border: 'none' }}>
                    <UsersGeography
                      initialData={currentData}
                      onDrillDown={handleDrillDown}
                      onBackClick={handleBackClick}
                      onTopClick={handleTopClick}
                      currentLevel={dataStack.length - 1}
                      levels={levels}
                      dataStack={dataStack}
                    />
                  </div>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, height: '350px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', minWidth: 0 }}>
                <CardContent sx={{ height: '100%', padding: '24px' }}>
                  <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    Users by Workshop
                  </Typography>
                  <div style={{ height: 'calc(100% - 40px)', width: '100%' }}>
                    <WorkshopUsers
                      data={workshopUsersData}
                      onWorkshopSelect={handleWorkshopSelect}
                      onClearWorkshop={handleClearWorkshop}
                      selectedWorkshop={selectedWorkshop}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <p>No data available for the selected date range.</p>
        )}
      </div>

    </>
  );
}
